// Hook backing the App Builder page. Loads the tenant's latest config,
// holds the working draft in local state, exposes save/publish mutations.
//
// "Working draft" lives client-side until the user clicks Save. This lets
// them experiment freely (move tabs around, try a new color) without each
// keystroke hitting the server. Auto-save to localStorage every 2s so a
// browser refresh doesn't lose work.

import { useEffect, useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type AppConfig,
  appConfigSchema,
  DEFAULT_APP_CONFIG,
  safeParseConfig,
} from '@erp/app-builder';
import { useDataService } from '@/lib/service-context';

const LS_KEY = 'app-builder/working-draft';
const QK = ['app-builder', 'tenant'] as const;

export interface BuilderState {
  loading: boolean;
  /** What the admin is editing right now. */
  draft: AppConfig;
  /** What's currently published to mobile clients. */
  published: AppConfig | null;
  /** Latest server-side draft (if any). */
  savedDraft: AppConfig | null;
  /** True when local edits diverge from saved/published state. */
  dirty: boolean;
  validationError: string | null;
  setDraft: (next: AppConfig) => void;
  resetToDefault: () => void;
  resetToPublished: () => void;
  save: () => Promise<void>;
  publish: () => Promise<void>;
  rollback: () => Promise<void>;
  isSaving: boolean;
  isPublishing: boolean;
}

export function useBuilderState(): BuilderState {
  const ds = useDataService();
  const qc = useQueryClient();

  const tenantQ = useQuery({
    queryKey: QK,
    queryFn: () => ds.appConfig.getTenantLatest(),
  });

  // Initial draft: localStorage > server draft > server published > defaults.
  const [draft, setDraftState] = useState<AppConfig | null>(null);

  useEffect(() => {
    if (draft !== null) return;
    if (tenantQ.isLoading) return;
    const cached = localStorage.getItem(LS_KEY);
    if (cached) {
      const parsed = safeParseConfig(JSON.parse(cached));
      if (parsed) {
        setDraftState(parsed);
        return;
      }
    }
    const row = tenantQ.data;
    const fromServer = row?.config ? safeParseConfig(row.config) : null;
    setDraftState(fromServer ?? DEFAULT_APP_CONFIG);
  }, [tenantQ.data, tenantQ.isLoading, draft]);

  // Auto-save to localStorage on every change.
  useEffect(() => {
    if (draft) {
      const id = setTimeout(() => {
        localStorage.setItem(LS_KEY, JSON.stringify(draft));
      }, 500);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [draft]);

  const published = useMemo<AppConfig | null>(() => {
    const row = tenantQ.data;
    if (!row || row.status !== 'published') return null;
    return safeParseConfig(row.config);
  }, [tenantQ.data]);

  const savedDraft = useMemo<AppConfig | null>(() => {
    const row = tenantQ.data;
    if (!row || row.status !== 'draft') return null;
    return safeParseConfig(row.config);
  }, [tenantQ.data]);

  const validationError = useMemo<string | null>(() => {
    if (!draft) return null;
    const r = appConfigSchema.safeParse(draft);
    if (r.success) return null;
    return r.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join(' · ');
  }, [draft]);

  const dirty = useMemo<boolean>(() => {
    if (!draft) return false;
    const reference = savedDraft ?? published ?? DEFAULT_APP_CONFIG;
    return JSON.stringify(draft) !== JSON.stringify(reference);
  }, [draft, savedDraft, published]);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!draft) return;
      const validated = appConfigSchema.parse(draft);
      await ds.appConfig.saveTenantDraft(validated);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });

  const publishMut = useMutation({
    mutationFn: async () => {
      if (!draft) return;
      const validated = appConfigSchema.parse(draft);
      await ds.appConfig.publishTenant(validated);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });

  const rollbackMut = useMutation({
    mutationFn: async () => { await ds.appConfig.rollbackTenant(); },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });

  return {
    loading: tenantQ.isLoading || draft === null,
    draft: draft ?? DEFAULT_APP_CONFIG,
    published,
    savedDraft,
    dirty,
    validationError,
    setDraft: setDraftState,
    resetToDefault: () => setDraftState(DEFAULT_APP_CONFIG),
    resetToPublished: () => setDraftState(published ?? DEFAULT_APP_CONFIG),
    save: () => saveMut.mutateAsync(),
    publish: () => publishMut.mutateAsync(),
    rollback: () => rollbackMut.mutateAsync(),
    isSaving: saveMut.isPending,
    isPublishing: publishMut.isPending,
  };
}
